import clsx from 'clsx';
import styles from './sign-in-otp-form.module.scss';
import { Button, InputRedesigned, Typography } from '@/ui-kit';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { authApi, profilesApi } from '@/api';
import { ErrorNotificationIcon, FillProfileIcon } from '@/assets/icons';
import { padStart } from 'lodash';
import otpImg from '@/assets/images/otp.png';
import { signInOtpSchema, SignInOtpType } from '@/schemes/sign-in-otp-schema';
import { AxiosError } from 'axios';
import { jwtService } from '@/services/jwt.service';
import { LangSelector } from '../lang-selector/lang-selector';
import { useUpdateUserData } from '@/hooks/use-update-profile';
import i18n from '@/i18n';
import { callYaMetrika } from '@/utils/call-ya-metrika';

interface Props {
    secondsBeforeOTPRequest: number | null,
    email: string,
    error: string | null,
    setError: Dispatch<SetStateAction<string | null>>,
    returnToEmailInput: () => void,
    showErrorToast: () => void,
    resendCode: () => Promise<void>,
    onLogin: () => void
}

export const SignInOtpForm: React.FC<Props> = ({
    secondsBeforeOTPRequest,
    email,
    error,
    setError,
    returnToEmailInput,
    showErrorToast,
    resendCode,
    onLogin,
}) => {
    const [isLoading, setIsLoading] = useState(false)

    const { t } = useTranslation()

    const { mutateAsync: updateUserData } = useUpdateUserData();

    const enterOtpInitialValues = useMemo(() => {
        return {
            otpFirstChar: '',
            otpSecondChar: '',
            otpThirdChar: '',
            otpFourthChar: '',
        };
    }, []);
    
    const enterOTPMethods = useForm<SignInOtpType>({
        resolver: zodResolver(signInOtpSchema),
        defaultValues: enterOtpInitialValues
    });
    
    const {
        register: enterOTPRegister,
        handleSubmit,
        formState: { isValid },
    } = enterOTPMethods;
    
    const onOtpInput = async (e: React.FormEvent<HTMLInputElement>, nextEl?: HTMLInputElement | null) => {
        if (e.currentTarget.value.length) {
            const nextEl = e.currentTarget.nextElementSibling
            const isNotLastFilledInput =
                nextEl && e.currentTarget.value.length
            if (
                isNotLastFilledInput &&
                nextEl instanceof HTMLInputElement
            ) nextEl.focus()
            if (!nextEl) e.currentTarget.blur()
        }
    }

    const onSubmit = async () => {
        setError(null)
        try {
            setIsLoading(true);
            const res = await authApi.verifyOTP(
                email,
                [
                    enterOTPMethods.getValues('otpFirstChar'),
                    enterOTPMethods.getValues('otpSecondChar'),
                    enterOTPMethods.getValues('otpThirdChar'),
                    enterOTPMethods.getValues('otpFourthChar'),
                ].join('')
            )
            if (res) {
                const resData = res.data
                jwtService.setAccessToken(resData.access_token)
                jwtService.setRefreshToken(resData.refresh_token)
                updateUserData({ lang: i18n.language });
                onLogin()
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                if (error.response?.status === 401)
                    setError(error.response.data.error)
                else showErrorToast();
            }
            enterOTPMethods.reset()
        } finally {
            setIsLoading(false);
        }
    }

    const onReturnToEmailInput = () => {
        if (isLoading) return;
        returnToEmailInput()
    }

    const onResendClick = async () => {
        setIsLoading(true);
        await resendCode()
        setIsLoading(false);
    }

    const fillZeroes = (val: string) => padStart(val, 2, '0')

    const displayTimeout = () => {
        if (!secondsBeforeOTPRequest) return '00:00'
        const mins = Math.floor(secondsBeforeOTPRequest / 60)
        return `${fillZeroes(mins.toString())}:${fillZeroes((secondsBeforeOTPRequest - mins * 60).toString())}`
    }
    
    useEffect(() => {
        callYaMetrika('code_screen_loaded')
    }, [])

    useEffect(() => {
        if (isValid) {
            const currentEl = document.activeElement
            if (currentEl instanceof HTMLInputElement)
                currentEl.blur()
            onSubmit()
        }
    }, [isValid])

    const INPUT_MAX_LEN = 1

    const otpFirstInputRegister = enterOTPRegister('otpFirstChar')
    const otpSecondInputRegister = enterOTPRegister('otpSecondChar')
    const otpThirdInputRegister = enterOTPRegister('otpThirdChar')
    const otpFourthInputRegister = enterOTPRegister('otpFourthChar')
    
    return (
        <FormProvider { ...enterOTPMethods }>
            <form className={styles.wrapper} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.content}>
                    <img className={styles.otpImg} src={otpImg} />
                    <div className={styles.emailInfo}>
                        <Typography
                            variant="span"
                            fontWeight="medium"
                            className={clsx(styles.titleForm, styles.otpTitleForm)}
                            center>
                            {t('auth.otp_sent_to', { email })}
                        </Typography>
                        <FillProfileIcon
                            width={16}
                            height={16}
                            className={styles.emailInfoEditBtn}
                            onClick={onReturnToEmailInput} />
                    </div>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.subtitleForm}
                        center>
                        {t('auth.input_for_continue')}
                    </Typography>
                    <div className={styles.otpInputView}>
                        <InputRedesigned
                            maxLength={INPUT_MAX_LEN}
                            className={clsx(styles.otpInput, error && styles.inputError)}
                            { ...otpFirstInputRegister }
                            onChange={e => {
                                otpFirstInputRegister.onChange(e)
                                onOtpInput(e)
                            }} />
                        <InputRedesigned
                            maxLength={INPUT_MAX_LEN}
                            className={clsx(styles.otpInput, error && styles.inputError)}
                            { ...otpSecondInputRegister }
                            onChange={e => {
                                otpSecondInputRegister.onChange(e)
                                onOtpInput(e)
                            }} />
                        <InputRedesigned
                            maxLength={INPUT_MAX_LEN}
                            className={clsx(styles.otpInput, error && styles.inputError)}
                            { ...otpThirdInputRegister }
                            onChange={e => {
                                otpThirdInputRegister.onChange(e)
                                onOtpInput(e)
                            }} />
                        <InputRedesigned
                            required
                            maxLength={INPUT_MAX_LEN}
                            className={clsx(styles.otpInput, error && styles.inputError)}
                            { ...otpFourthInputRegister }
                            onChange={e => {
                                otpFourthInputRegister.onChange(e)
                                onOtpInput(e)
                            }} />
                    </div>
                    {
                        error &&
                        <div className={clsx(styles.error, styles.otpError)}>
                            <ErrorNotificationIcon className={styles.errorIcon} />
                            <Typography className={styles.errorLabel}>
                                {error}
                            </Typography>

                        </div>
                    }
                    <div className={styles.actions}>
                        {
                            secondsBeforeOTPRequest ?
                                <Button
                                    textColor="black"
                                    className={styles.resendCodeInfo}
                                    isLoading={isLoading}
                                    isDisabled={isLoading}>{t('auth.send_new_code_timeout', { timeout: displayTimeout() })}</Button>
                            :
                                <Button
                                    type="submit"
                                    variant="stretched"
                                    typeColor="white"
                                    textColor={'white'}
                                    className={
                                        clsx(
                                            styles.submitBtn,
                                            isLoading && styles.submitBtnDisabled
                                        )
                                    }
                                    isLoading={isLoading}
                                    isDisabled={isLoading}
                                    onClick={onResendClick}>{t('auth.send_new_code')}</Button>
                        }
                    </div>
                </div>
                <LangSelector className={styles.langSelector} />
            </form>
        </FormProvider>
    );
}
